import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import {connect} from 'react-redux';
import _ from 'underscore';

import {BenchlingState} from 'redux-stores/reduceApp';
import {BenchlingDispatch} from 'redux-stores/Store';
import AbsoluteDropdownContainer from 'ui-library/AbsoluteDropdownContainer';
import AddIcon from 'ui-library/icons/AddIcon';
import runModal from 'ui-library/runModal';
import ConfigureStageFlowNodeModal from 'workflows/modals/ConfigureStageFlowNodeModal';
import SetExperimentalConditionsModal from 'workflows/modals/SetExperimentalConditionsModal';
import * as StageFlowNodeActions from 'workflows/StageFlowNodeActions';
import {stageConfigurationHasExperimentalConditionValues} from 'workflows/util/ExperimentalConditionUtil';
import {
  STAGE_CONFIGURATION_FULL_SHAPE,
  STAGE_FLOW_NODE_FULL_SHAPE,
  StageConfiguration,
  StageFlowNode,
} from 'workflows/WorkflowConstants';

import {GraphLayout} from './GraphLayout';
import {STAGE_RUN_RADIUS, STAGE_WIDTH} from './GraphLayoutConstants';
import StageFlowNodeDropdownMenu from './StageFlowNodeDropdownMenu';
import {getLinkHighlightMode, getNodeHighlightMode, HighlightMode} from './StageFlowNodeHighlightUtil';
import StageFlowNodeVisualization, {
  StageFlowNodeSecondaryLinkVisualization,
} from './StageFlowNodeVisualization';
import {StageRunLinkVisualization} from './StageRunVisualization';
import StageVisualization from './StageVisualization';
import {StageFlowNodeGraph} from './TaggedNodeGraph';
import * as WorkflowVisualizationReduxActions from './WorkflowVisualizationReduxActions';

function mapStateToProps(state: BenchlingState): ConnectedProps {
  return {
    clickedUpstreamNodeId: state.ui.workflowVisualization.clickedUpstreamNodeId,
    clickedDownstreamNodeId: state.ui.workflowVisualization.clickedDownstreamNodeId,
    rightClickedNodeId: state.ui.workflowVisualization.rightClickedNodeId,
    hoveredNodeId: state.ui.workflowVisualization.hoveredNodeId,
  };
}

function mapDispatchToProps(dispatch: BenchlingDispatch): DispatchProps {
  return {
    onReset(): void {
      dispatch(WorkflowVisualizationReduxActions.reset());
    },
    onClickNode(nodeId: string): void {
      dispatch(WorkflowVisualizationReduxActions.clickNode(nodeId));
    },
    onClickLink(parentNodeId: string | null, childNodeId: string | null): void {
      dispatch(WorkflowVisualizationReduxActions.clickLink(parentNodeId, childNodeId));
    },
    onRightClickNode(nodeId: string): void {
      dispatch(WorkflowVisualizationReduxActions.rightClickNode(nodeId));
    },
    onClearRightClick(): void {
      dispatch(WorkflowVisualizationReduxActions.rightClickNode(null));
    },
    onHoverNode(nodeId: string): void {
      dispatch(WorkflowVisualizationReduxActions.hoverNode(nodeId));
    },
    onUnhoverNode(nodeId: string): void {
      dispatch(WorkflowVisualizationReduxActions.unhoverNode(nodeId));
    },
  };
}

interface BaseWorkflowConfigurationVisualizationProps {
  containerWidth: number;
  isEditable: boolean;
  stageConfigurations: Array<StageConfiguration>;
  stageConfigurationIdToStageFlowNodes: {[stageConfigurationId: string]: Array<StageFlowNode>};
}

interface ConnectedProps {
  clickedUpstreamNodeId: string | null;
  clickedDownstreamNodeId: string | null;
  rightClickedNodeId: string | null;
  hoveredNodeId: string | null;
}

interface DispatchProps {
  onReset: () => void;
  onClickNode: (nodeId: string) => void;
  onClickLink: (parentNodeId: string | null, childNodeId: string | null) => void;
  onRightClickNode: (nodeId: string) => void;
  onClearRightClick: () => void;
  onHoverNode: (nodeId: string) => void;
  onUnhoverNode: (nodeId: string) => void;
}

class WorkflowConfigurationVisualization extends React.Component<
  BaseWorkflowConfigurationVisualizationProps & ConnectedProps & DispatchProps
> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static propTypes: any = {
    // Props to specify when rendering.
    containerWidth: PropTypes.number.isRequired,
    isEditable: PropTypes.bool.isRequired,
    stageConfigurations: PropTypes.arrayOf(STAGE_CONFIGURATION_FULL_SHAPE.isRequired).isRequired,
    stageConfigurationIdToStageFlowNodes: PropTypes.objectOf(
      PropTypes.arrayOf(STAGE_FLOW_NODE_FULL_SHAPE.isRequired)
    ).isRequired,

    // Props provided by connect.
    clickedUpstreamNodeId: PropTypes.string,
    clickedDownstreamNodeId: PropTypes.string,
    hoveredNodeId: PropTypes.string,
    rightClickedNodeId: PropTypes.string,
    onReset: PropTypes.func.isRequired,
    onClickNode: PropTypes.func.isRequired,
    onClickLink: PropTypes.func.isRequired,
    onRightClickNode: PropTypes.func.isRequired,
    onClearRightClick: PropTypes.func.isRequired,
    onHoverNode: PropTypes.func.isRequired,
    onUnhoverNode: PropTypes.func.isRequired,
  };

  _handleClickSetParent(stageFlowNode: StageFlowNode): void {
    const currentStageConfiguration = this.props.stageConfigurations.find((stageConfiguration) =>
      _.findWhere(stageConfiguration.stageFlowNodes, {id: stageFlowNode.id})
    )!;
    ConfigureStageFlowNodeModal.open({
      stageFlowNode,
      stageConfigurationIndex: currentStageConfiguration.index,
      stageConfigurations: this.props.stageConfigurations,
      stageConfigurationIdToStageFlowNodes: this.props.stageConfigurationIdToStageFlowNodes,
    });
  }

  async _handleOpenSetExperimentalCondition(
    stageFlowNode: StageFlowNode,
    stageConfiguration: StageConfiguration
  ): Promise<void> {
    await runModal(SetExperimentalConditionsModal, {
      stageFlowNode,
      stageConfiguration,
      onRequestSubmit: (experimentalConditionValueIds) => {
        StageFlowNodeActions.editStageFlowNode(stageFlowNode.id, {experimentalConditionValueIds});
      },
    });
  }

  _handleDeleteNode(
    stageFlowNode: StageFlowNode,
    {shouldDeleteDownstreamNodes}: {shouldDeleteDownstreamNodes: boolean}
  ): void {
    StageFlowNodeActions.deleteStageFlowNode(stageFlowNode.id, {shouldDeleteDownstreamNodes});
  }

  _handleCreateNode(stageConfiguration: StageConfiguration): void {
    ConfigureStageFlowNodeModal.open({
      stageConfigurationIndex: stageConfiguration.index,
      stageConfigurations: this.props.stageConfigurations,
      stageConfigurationIdToStageFlowNodes: this.props.stageConfigurationIdToStageFlowNodes,
    });
  }

  render(): JSX.Element {
    const {
      containerWidth,
      isEditable,
      stageConfigurations,
      rightClickedNodeId,
      onReset,
      onRightClickNode,
      hoveredNodeId,
      onHoverNode,
      onUnhoverNode,
      stageConfigurationIdToStageFlowNodes,
      onClearRightClick,
      clickedUpstreamNodeId,
      clickedDownstreamNodeId,
      onClickNode,
      onClickLink,
    } = this.props;
    // eslint-disable-next-line no-restricted-syntax
    const stageFlowNodes = _.flatten(Object.values(stageConfigurationIdToStageFlowNodes));
    const stageFlowNodeGraph = new StageFlowNodeGraph(stageFlowNodes, stageConfigurations);
    const graphLayout = new GraphLayout(stageFlowNodeGraph, STAGE_RUN_RADIUS);
    const treeHeight = graphLayout.getTreeHeight();
    const totalWidth = graphLayout.getTotalWidth(containerWidth);
    const totalHeight = graphLayout.getTotalHeight(containerWidth);
    const stageFlowNodeLinks = graphLayout.getLinks({
      leftBoundary: 0,
      rightBoundary: totalWidth,
    });

    if (!stageConfigurations.length) {
      return (
        <div style={{width: totalWidth, height: totalHeight}}>
          <div className="workflowVisualization-stages">
            <div className="u-position-relative" style={{width: STAGE_WIDTH, height: totalHeight}}>
              <div className="workflowVisualization-stage-header">
                <div className="workflowVisualization-stage-name">No stages</div>
              </div>
            </div>
          </div>
          <div className="workflowVisualization-arms">
            <svg width={totalWidth} height={1}>
              <path className="stageRunLinkVisualization-line" d={`M 0 0 H ${totalWidth}`} />
            </svg>
          </div>
        </div>
      );
    }
    return (
      <div style={{width: totalWidth, height: totalHeight}} onClick={onReset}>
        <div className="workflowVisualization-stages">
          {stageConfigurations.map((stageConfiguration) => (
            <StageVisualization
              key={stageConfiguration.id}
              name={stageConfiguration.stageName}
              height={totalHeight}
              width={graphLayout.getTagWidth(stageConfiguration.index)}
              icon={
                this.props.isEditable ? (
                  <AddIcon
                    className="stageVisualization-icon"
                    onClick={(e) => {
                      this._handleCreateNode(stageConfiguration);
                    }}
                  />
                ) : null
              }
            />
          ))}
        </div>
        <div
          className={classNames('workflowVisualization-arms', {
            'is-tall': graphLayout.isTallVisualization(),
          })}
        >
          <svg width={totalWidth} height={treeHeight}>
            {stageFlowNodeLinks.map(
              ({key, leftPosition, rightPosition, parentNodeId, childNodeId, isSecondary}) => {
                const StageRunLinkComponent = isSecondary
                  ? StageFlowNodeSecondaryLinkVisualization
                  : StageRunLinkVisualization;
                return (
                  <StageRunLinkComponent
                    key={key}
                    highlightMode={getLinkHighlightMode(parentNodeId, childNodeId, {
                      clickedUpstreamNodeId,
                      clickedDownstreamNodeId,
                      taggedNodeGraph: stageFlowNodeGraph,
                    })}
                    onClick={(e: React.MouseEvent<SVGPathElement>) => {
                      e.stopPropagation();
                      onClickLink(parentNodeId, childNodeId);
                    }}
                    isDiscarded={false}
                    leftPosition={leftPosition}
                    rightPosition={rightPosition}
                  />
                );
              }
            )}
            {stageFlowNodeGraph.getNodeIds().map((nodeId) => {
              const highlightMode = getNodeHighlightMode(nodeId, {
                clickedUpstreamNodeId,
                clickedDownstreamNodeId,
                taggedNodeGraph: stageFlowNodeGraph,
              });
              const stageFlowNode = stageFlowNodeGraph.getStageFlowNode(nodeId);
              const stageConfiguration = stageFlowNodeGraph.getStageConfiguration(nodeId);
              return (
                <AbsoluteDropdownContainer
                  key={stageFlowNode.id}
                  menu={
                    <StageFlowNodeDropdownMenu
                      onClickDuplicateTree={(e) => {
                        StageFlowNodeActions.duplicateStageFlowNodeTree({
                          stageFlowNodeId: stageFlowNode.id,
                        });
                      }}
                      onClickExplodeTree={(e) => {
                        StageFlowNodeActions.explodeStageFlowNodeTree({stageFlowNodeId: stageFlowNode.id});
                      }}
                      onClickSetParent={(e) => {
                        this._handleClickSetParent(stageFlowNode);
                      }}
                      onRemoveNode={(e) => {
                        this._handleDeleteNode(stageFlowNode, {shouldDeleteDownstreamNodes: false});
                      }}
                      onRemoveNodeAndDownstreamNodes={(e) => {
                        this._handleDeleteNode(stageFlowNode, {shouldDeleteDownstreamNodes: true});
                      }}
                      onClickSetExperimentalCondition={(e) => {
                        this._handleOpenSetExperimentalCondition(stageFlowNode, stageConfiguration);
                      }}
                      openSetExperimentalConditionDisabled={
                        !stageConfigurationHasExperimentalConditionValues(stageConfiguration)
                      }
                    />
                  }
                  onRequestClose={onClearRightClick}
                  open={stageFlowNode.id === rightClickedNodeId}
                  shouldCloseOnInsideClick={true}
                >
                  <StageFlowNodeVisualization
                    highlightMode={highlightMode}
                    isNodeClickable={true}
                    experimentalConditionValues={stageFlowNode.experimentalConditionValues}
                    position={graphLayout.getNodePosition(stageFlowNode.id)}
                    stageWidth={graphLayout.getTagWidth(stageFlowNodeGraph.getTag(nodeId))}
                    relativeX={graphLayout.getRelativeX(nodeId)}
                    isHovered={stageFlowNode.id === hoveredNodeId}
                    onHover={() => {
                      onHoverNode(stageFlowNode.id);
                    }}
                    onUnhover={() => {
                      onUnhoverNode(stageFlowNode.id);
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onClickNode(stageFlowNode.id);
                    }}
                    onRightClick={(e: React.MouseEvent<SVGGElement>) => {
                      e.preventDefault();
                      if (isEditable) {
                        if (highlightMode !== HighlightMode.HIGHLIGHTED) {
                          this.props.onClickNode(stageFlowNode.id);
                        }
                        onRightClickNode(stageFlowNode.id);
                      }
                    }}
                    shouldShowExperimentalCondition={stageFlowNodeGraph.isLastDescendantWithTag(
                      stageFlowNode.id
                    )}
                  />
                </AbsoluteDropdownContainer>
              );
            })}
          </svg>
        </div>
      </div>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(WorkflowConfigurationVisualization);
